import { LitElement, html, css, TemplateResult } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { virtualize } from '@lit-labs/virtualizer/virtualize.js';

import { nsSharedStyles } from '../ns-styles/index.js';

export interface EditableTableColumn {
  key: string;
  label: string;
  type: 'text' | 'email' | 'select';
  options?: { value: string; label: string }[];
}

export type EditableTableRow = { [key: string]: string };

/**
 * General-purpose inline data-entry grid: a fixed header plus N rows, always ending in one blank
 * row that auto-appends a fresh blank row the moment it stops being blank — so the grid grows as
 * you type with no explicit "add row" affordance. Rows are virtualized (`@lit-labs/virtualizer`,
 * same pattern as `ns-feed.ts`) since consumers can realistically have thousands of rows (e.g. a
 * bulk CSV import) — only visible rows get real DOM.
 */
@customElement('ns-editable-table')
export class NSEditableTable extends LitElement {
  @property({ type: Array })
  columns: EditableTableColumn[] = [];

  @property({ type: Number })
  minRows = 8;

  @state()
  private __rows: EditableTableRow[] = [];

  connectedCallback() {
    super.connectedCallback();
    this.__rows = Array.from({ length: Math.max(this.minRows, 1) }, () => this.__blankRow());
  }

  private __blankRow(): EditableTableRow {
    return Object.fromEntries(this.columns.map((column) => [column.key, '']));
  }

  private __isRowBlank(row: EditableTableRow): boolean {
    return Object.values(row).every((value) => !value.trim());
  }

  private __onCellInput(rowIndex: number, key: string, value: string) {
    const rows = [...this.__rows];
    rows[rowIndex] = { ...rows[rowIndex], [key]: value };

    if (rowIndex === rows.length - 1 && !this.__isRowBlank(rows[rowIndex])) {
      rows.push(this.__blankRow());
    }

    this.__setRows(rows);
  }

  private __setRows(rows: EditableTableRow[]) {
    this.__rows = rows;
    this.dispatchEvent(
      new CustomEvent('rows-changed', {
        detail: { rows: rows.filter((row) => !this.__isRowBlank(row)) },
        bubbles: true,
        composed: true,
      }),
    );
  }

  /** Appends externally-sourced rows (e.g. a parsed CSV import) after whatever's already filled
   * in, keeping one trailing blank row. Existing rows are left untouched — this adds to the
   * table rather than replacing it. */
  public appendRows(newRows: EditableTableRow[]) {
    const filled = this.__rows.filter((row) => !this.__isRowBlank(row));
    this.__setRows([...filled, ...newRows, this.__blankRow()]);
  }

  /** Recycled virtualizer nodes can silently swap which row an already-focused input represents
   * mid-scroll — blur defensively so a user never ends up typing into a row they can't see. */
  private __onBodyScroll = () => {
    const active = this.shadowRoot?.activeElement as HTMLElement | null;
    if (active && (active.tagName === 'INPUT' || active.tagName === 'SELECT')) {
      active.blur();
    }
  };

  static styles = [
    nsSharedStyles,
    css`
      :host {
        display: block;
      }

      .grid {
        display: flex;
        flex-direction: column;
      }

      .header-row {
        display: flex;
        gap: var(--wa-space-xs);

        /* must match .body's padding (4px) + .row's padding (6px) so columns line up */
        padding: 0 var(--wa-space-xs) var(--wa-space-xs);
      }

      .header-cell {
        flex: 1;
        min-width: 0;
        overflow: hidden;

        display: flex;
        justify-content: flex-start;
      }

      .header-pill {
        display: inline-block;
        max-width: 100%;
        box-sizing: border-box;

        background: var(--ns-theme-green);
        color: var(--ns-theme-type);

        border-radius: var(--wa-border-radius-pill);
        padding: var(--wa-space-xs) var(--wa-space-m);

        font-size: 0.85em;
        font-weight: 600;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      .body {
        /* ~45px per row + 8px gap — sized for ~5-6 visible rows before scrolling. A fixed height, since the
           virtualizer scrolls the body and can't size it from rows it hasn't drawn */
        height: 300px;
        overflow-y: auto;

        padding: 0 var(--wa-space-2xs) var(--wa-space-2xs);
      }

      .row {
        display: flex;
        gap: var(--wa-space-xs);

        /* virtualizer positions items itself, so spacing must be margin, not .body's flex gap */
        margin-bottom: var(--wa-space-xs);

        background: var(--ns-theme-grey-mid);
        border-radius: var(--wa-border-radius-l);
        padding: var(--wa-space-2xs);
      }

      .cell {
        flex: 1;
        min-width: 0;

        display: flex;
        align-items: center;

        background: var(--ns-theme-container);
        border-radius: var(--wa-border-radius-pill);
      }

      input {
        width: 100%;
        min-width: 0;
        box-sizing: border-box;

        background: none;
        color: var(--ns-theme-type);
        border: none;
        outline: none;

        padding: var(--wa-space-xs) var(--wa-space-m);

        font-size: 0.9em;
        font-family: inherit;

        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      .select-cell {
        position: relative;

        justify-content: space-between;

        padding: var(--wa-space-2xs) var(--wa-space-2xs) var(--wa-space-2xs) var(--wa-space-m);
      }

      .select-value-pill {
        min-width: 0;

        background: var(--ns-theme-grey-mid);
        color: var(--ns-theme-type);

        border-radius: var(--wa-border-radius-pill);
        padding: var(--wa-space-2xs) var(--wa-space-s);

        font-size: 0.85em;

        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      .chevron-button {
        flex: 0 0 auto;

        width: 22px;
        height: 22px;
        border-radius: var(--wa-border-radius-circle);

        background: var(--ns-theme-grey-mid);

        display: flex;
        align-items: center;
        justify-content: center;
      }

      .chevron-button::after {
        content: '';

        width: 0;
        height: 0;

        border-left: 4px solid transparent;
        border-right: 4px solid transparent;
        border-top: 5px solid var(--ns-theme-grey-dark);
      }

      .select-cell select {
        position: absolute;
        inset: 0;

        width: 100%;
        height: 100%;

        opacity: 0;
        cursor: pointer;
      }

      input:focus,
      .cell:has(select:focus) {
        outline: 2px solid var(--primary-color);
        outline-offset: -2px;
      }
    `,
  ];

  private __selectedOptionLabel(column: EditableTableColumn, value: string): string {
    return column.options?.find((option) => option.value === value)?.label ?? '';
  }

  private __renderRow(row: EditableTableRow, rowIndex: number): TemplateResult {
    return html`
      <div class="row" role="row">
        ${this.columns.map(
          (column) => html`
            ${column.type === 'select'
              ? html`
                  <div class="cell select-cell" role="cell">
                    <span class="select-value-pill">${this.__selectedOptionLabel(column, row[column.key])}</span>
                    <span class="chevron-button" aria-hidden="true"></span>
                    <select
                      aria-label=${column.label}
                      .value=${row[column.key]}
                      @change=${(e: Event) =>
                        this.__onCellInput(rowIndex, column.key, (e.target as HTMLSelectElement).value)}
                    >
                      <option value=""></option>
                      ${(column.options ?? []).map(
                        (option) => html`
                          <option value=${option.value}>${option.label}</option>
                        `,
                      )}
                    </select>
                  </div>
                `
              : html`
                  <div class="cell" role="cell">
                    <input
                      type=${column.type}
                      aria-label=${column.label}
                      .value=${row[column.key]}
                      @input=${(e: Event) =>
                        this.__onCellInput(rowIndex, column.key, (e.target as HTMLInputElement).value)}
                    />
                  </div>
                `}
          `,
        )}
      </div>
    `;
  }

  render() {
    return html`
      <div class="grid" role="table">
        <div class="header-row" role="row">
          ${this.columns.map(
            (column) => html`
              <div class="header-cell">
                <div class="header-pill" role="columnheader" title=${column.label}>${column.label}</div>
              </div>
            `,
          )}
        </div>
        <div class="body" @scroll=${this.__onBodyScroll}>
          ${virtualize({
            items: this.__rows,
            renderItem: (row, rowIndex) => this.__renderRow(row, rowIndex),
            // The body scrolls itself; otherwise the virtualizer grows it to every row's height, so the card around it
            // scrolls instead and rows past the first screen are never drawn
            scroller: true,
          })}
        </div>
      </div>
    `;
  }
}
